import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { useResource } from '../../hooks';
import { currentEndpoint, disablePush, enablePush, pushSupported } from '../../push';
import { formatDate } from '../../time';

export function Notifications() {
  const devices = useResource(() => api.push.devices(), []);
  const publicKey = useResource(() => api.push.publicKey(), []);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void currentEndpoint().then(setEndpoint);
  }, [devices.data]);

  const registered = devices.data?.some((device) => device.endpoint === endpoint) ?? false;

  async function run(action: () => Promise<string>) {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      devices.reload();
      setEndpoint(await currentEndpoint());
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Notifications sur cet appareil</h2>

      {!pushSupported && (
        <p className="empty">
          {window.isSecureContext
            ? 'Ce navigateur ne gère pas les notifications.'
            : 'Les notifications exigent une connexion sécurisée (HTTPS) — elles seront disponibles une fois l’application déployée.'}
        </p>
      )}

      {pushSupported && publicKey.data && publicKey.data.publicKey === null && (
        <p className="empty">
          Le serveur n’a pas de clés VAPID : aucune notification ne peut être envoyée.
        </p>
      )}

      {pushSupported && (
        <div className="form__actions">
          {registered ? (
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={() =>
                void run(async () => {
                  await disablePush();
                  return 'Notifications désactivées sur cet appareil.';
                })
              }
            >
              Désactiver sur cet appareil
            </button>
          ) : (
            <button
              type="button"
              className="button button--primary"
              disabled={pending}
              onClick={() =>
                void run(async () => {
                  await enablePush();
                  return 'Cet appareil recevra les notifications.';
                })
              }
            >
              Activer sur cet appareil
            </button>
          )}

          <button
            type="button"
            className="button"
            disabled={pending}
            onClick={() =>
              void run(async () => {
                const { sent, failed } = await api.push.test();
                if (sent === 0) return 'Aucune notification envoyée : aucun appareil joignable.';
                return `Notification envoyée à ${sent} appareil${sent > 1 ? 's' : ''}${failed > 0 ? `, ${failed} en échec` : ''}.`;
              })
            }
          >
            Envoyer une notification de test
          </button>
        </div>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="page__note">{notice}</p>}

      <h3 className="panel__title panel__title--sub">Appareils enregistrés</h3>
      {devices.error && (
        <p className="error" role="alert">
          {devices.error}
        </p>
      )}
      {devices.data?.length === 0 && <p className="empty">Aucun appareil enregistré.</p>}

      <ul className="list">
        {(devices.data ?? []).map((device) => (
          <li key={device.id}>
            <div className="list__row">
              <span className="list__lead">{device.deviceLabel ?? 'Appareil'}</span>
              <span className="muted">
                depuis le {formatDate(device.createdAt.slice(0, 10))}
                {device.endpoint === endpoint && ' · cet appareil'}
              </span>
              <span className="list__actions">
                <button
                  type="button"
                  className="button button--small button--danger"
                  disabled={pending}
                  onClick={() =>
                    void run(async () => {
                      if (device.endpoint === endpoint) await disablePush();
                      else await api.push.removeDevice(device.id);
                      return 'Appareil supprimé.';
                    })
                  }
                >
                  Supprimer
                </button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
